import React, { useState, useContext, useEffect } from 'react';
import { AppBar, Toolbar, Typography, IconButton, Drawer, Avatar, Box } from '@mui/material';
import { ArrowBack, Menu, LightMode, DarkMode, Dashboard, Visibility, People, GridView, Business, Settings, Logout, Assessment, AccountBalanceWallet } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { WebSocketContext } from "../WebSocketContext";
import { useTheme } from '../contexts/ThemeContext';
import { removeToken } from '../utils/auth';
import './TitleBarMaster.css';

function TitleBarMaster() {
  const { userDetails } = useContext(WebSocketContext);
  const { isDarkMode, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Debug role
  const role = localStorage.getItem('role');
//   console.log('TitleBarMaster - Role from localStorage:', role);
//   console.log('TitleBarMaster - All localStorage:', localStorage);

  // Real-time clock updates
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const toggleDrawer = (open) => () => {
    setDrawerOpen(open);
  };

  const handleNavigation = (path) => () => {
    navigate(path);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    removeToken();
    navigate('/login');
    setDrawerOpen(false);
  };

  const showBackButton = false;
  // location.pathname !== '/master/dashboard' && 
  //                       location.pathname !== '/admin/dashboard' && 
  //                       location.pathname !== '/bank/dashboard' && 
  //                       location.pathname !== '/broker/dashboard';

  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/master/')) return 'Master Panel';
    if (path.includes('/admin/')) return 'Admin Panel';
    if (path.includes('/bank/')) return 'Bank Panel';
    if (path.includes('/broker/')) return 'Broker Panel';
    return 'Panel';
  };

  const getRoleDisplay = () => {
    const role = localStorage.getItem('role') || 'E'; // Default to End User if null
    switch(role) {
      case 'A': return 'Admin';
      case 'M': return 'Master';
      case 'B': return 'Bank';
      case 'BR': return 'Broker';
      case 'E': return 'End User';
      default: return 'User';
    }
  };

  const getNavigationItems = () => {
    const role = localStorage.getItem('role') || 'M'; // Default to Master if null
    
    // Handle different role mappings
    let basePath;
    switch(role.toLowerCase()) {
      case 'm':
        basePath = '/master';
        break;
      case 'a':
        basePath = '/admin';
        break;
      case 'b':
        basePath = '/bank';
        break;
      case 'br':
        basePath = '/broker';
        break;
      default:
        basePath = '/master';
    }
    
    return [
      { icon: <Dashboard />, label: 'Dashboard', path: `${basePath}/dashboard`, hasSubmenu: false },
      { icon: <Assessment />, label: 'Trades', path: `${basePath}/trades`, hasSubmenu: false },
      { icon: <AccountBalanceWallet />, label: 'Net Positions', path: `${basePath}/net-positions`, hasSubmenu: false },
      { icon: <Visibility />, label: 'Trading', path: `${basePath}/trading`, hasSubmenu: true },
      { icon: <People />, label: 'Users', path: `${basePath}/users`, hasSubmenu: true },
      { icon: <GridView />, label: 'Utilities', path: `${basePath}/utilities`, hasSubmenu: true },
      { icon: <Business />, label: 'Accounts', path: `${basePath}/accounts`, hasSubmenu: true },
      { icon: <Settings />, label: 'Settings', path: `${basePath}/settings`, hasSubmenu: true }
    ];
  };

  return (
    <>
      {/* Master AppBar */}
      <AppBar 
        position="fixed" 
        style={{ 
          backgroundColor: '#000000',
          boxShadow: 'none',
          color: '#ffffff',
          borderBottom: '1px solid #333333'
        }}
      >
        <Toolbar style={{ minHeight: '60px' }}>
          {showBackButton && (
            <IconButton edge="start" color="inherit" onClick={() => navigate(-1)}>
              <ArrowBack />
            </IconButton>
          )}
          {!showBackButton && (
            <IconButton edge="start" color="inherit" onClick={toggleDrawer(true)}>
              <Menu />
            </IconButton>
          )}
          <Typography variant="h6" style={{ fontSize: '20px', fontFamily: 'Lato', fontWeight: 600 }}>
            {getPageTitle()}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Master Drawer */}
      <Drawer 
        anchor="left" 
        open={drawerOpen} 
        onClose={toggleDrawer(false)}
        transitionDuration={300}
        PaperProps={{
          sx: {
            backgroundColor: '#000000',
            color: '#ffffff',
            width: '280px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
          }
        }}
      >
        {/* Header Section */}
        <div style={{
          padding: '20px 16px',
          borderBottom: '1px solid #333333',
          background: '#000000'
        }}>
          {/* Close Button */}
          <button
            onClick={toggleDrawer(false)}
            style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              background: 'none',
              border: 'none',
              color: '#ffffff',
              fontSize: '24px',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#333333';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            ×
          </button>

          {/* User Info */}
          {userDetails && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px'
            }}>
              <Avatar
                sx={{
                  width: 50,
                  height: 50,
                  bgcolor: '#4caf50',
                  color: '#ffffff',
                  fontSize: '18px',
                  fontWeight: '600'
                }}
              >
                {userDetails.clientName && userDetails.clientName.split(" ").map((n) => n[0]).join("").toUpperCase()}
              </Avatar>
              <div>
                <Typography variant="h6" sx={{ 
                  fontWeight: 600, 
                  color: '#ffffff',
                  fontSize: '16px',
                  marginBottom: '2px',
                  lineHeight: 1.2
                }}>
                  {userDetails.clientName}
                </Typography>
                <Typography variant="body2" sx={{ 
                  color: '#aaaaaa',
                  fontSize: '12px'
                }}>
                  {getRoleDisplay()} • ID: {userDetails.clientId}
                </Typography>
              </div>
            </div>
          )}

          {/* Real-time Clock */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: '#2a2a2a',
            borderRadius: '8px'
          }}>
            <div style={{
              fontSize: '14px',
              fontWeight: '500',
              color: '#ffffff'
            }}>
              {currentTime.toLocaleTimeString('en-US', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: false 
              })}
            </div>
            <div style={{
              fontSize: '12px',
              color: '#cccccc'
            }}>
              {currentTime.toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'short', 
                day: 'numeric' 
              })}
            </div>
          </div>
        </div>

        {/* Navigation Section */}
        <div style={{
          padding: '8px 0',
          flex: 1,
          overflowY: 'auto'
        }}>
          {/* Main Navigation */}
          <div style={{ padding: '8px 0' }}>
            <div style={{
              padding: '12px 16px',
              fontSize: '12px',
              fontWeight: 600,
              color: '#888888',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              {getRoleDisplay()} Panel
            </div>
            
            {getNavigationItems().map((item, index) => (
              <div
                key={index}
                onClick={handleNavigation(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderLeft: '3px solid transparent',
                  fontSize: '14px',
                  minHeight: '48px',
                  backgroundColor: location.pathname === item.path ? 
                    '#2d2d2d' : 'transparent',
                  borderLeftColor: location.pathname === item.path ? 
                    '#4caf50' : 'transparent'
                }}
                onMouseOver={(e) => {
                  if (location.pathname !== item.path) {
                    e.target.style.backgroundColor = '#2d2d2d';
                    e.target.style.borderLeftColor = '#4caf50';
                  }
                }}
                onMouseOut={(e) => {
                  if (location.pathname !== item.path) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderLeftColor = 'transparent';
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ 
                    fontSize: '20px', 
                    marginRight: '12px', 
                    width: '24px',
                    color: location.pathname === item.path ? '#4caf50' : '#ffd700'
                  }}>
                    {item.icon}
                  </span>
                  <span style={{ 
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#ffffff'
                  }}>
                    {item.label}
                  </span>
                </div>
                {item.hasSubmenu && (
                  <span style={{ 
                    fontSize: '12px',
                    color: '#ffffff',
                    fontWeight: 'bold'
                  }}>
                    &lt;
                  </span>
                )}
              </div>
            ))}
          </div>

          {/* Theme Toggle */}
          <div style={{ padding: '4px 0' }}>
            <div style={{
              padding: '8px 16px',
              fontSize: '11px',
              fontWeight: 600,
              color: '#888888',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              marginBottom: '4px'
            }}>
              Appearance
            </div>
            
            <div
              onClick={toggleTheme}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderLeft: '2px solid transparent'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#2d2d2d';
                e.target.style.borderLeftColor = '#4caf50';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderLeftColor = 'transparent';
              }}
            >
              <span style={{ fontSize: '16px', marginRight: '12px', width: '20px' }}>
                {isDarkMode ? '🌞' : '🌙'}
              </span>
              <span style={{ 
                fontSize: '13px',
                fontWeight: 500,
                color: '#ffffff'
              }}>
                {isDarkMode ? 'Light Mode' : 'Dark Mode'}
              </span>
            </div>
          </div>

          {/* Logout Section */}
          <div style={{
            padding: '12px 16px',
            borderTop: '1px solid #333333',
            marginTop: '8px'
          }}>
            <div
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                padding: '12px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderLeft: '3px solid transparent',
                fontSize: '14px',
                minHeight: '48px',
                backgroundColor: 'transparent'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#2d2d2d';
                e.target.style.borderLeftColor = '#f44336';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.borderLeftColor = 'transparent';
              }}
            >
              <span style={{ 
                fontSize: '20px', 
                marginRight: '12px', 
                width: '24px',
                color: '#f44336'
              }}>
                <Logout />
              </span>
              <span style={{ 
                fontSize: '14px',
                fontWeight: 500,
                color: '#f44336'
              }}>
                Logout
              </span>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default TitleBarMaster;
